import { PersonalAccountPurpose, PhoneLoginKind, type PhoneLoginAccountCandidate } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessage, NoddiButton, type ErrorCodes } from 'noddi-ui-web';
import { commonTexts, getPhoneLoginOutcome, type PhoneLoginSession } from 'noddi-util';
import { useState } from 'react';

type AccountSelectionTranslations = {
  chooseAccount: string;
  chooseAccountDescription: string;
  back: string;
  personalAccountPrivate: string;
  personalAccountBusiness: string;
};

function purposeLabel(
  purpose: PhoneLoginAccountCandidate['personalAccountPurpose'],
  texts: AccountSelectionTranslations
): string | null {
  if (purpose == null) {
    return null;
  }
  const labels: Record<PersonalAccountPurpose, string> = {
    [PersonalAccountPurpose.PRIVATE]: texts.personalAccountPrivate,
    [PersonalAccountPurpose.BUSINESS]: texts.personalAccountBusiness
  };
  return labels[purpose];
}

type Props = {
  accountSelectionToken: string;
  accounts: PhoneLoginAccountCandidate[];
  onSelected: (session: PhoneLoginSession) => void;
  onBack: () => void;
  translations?: Partial<AccountSelectionTranslations>;
  errorCodes?: ErrorCodes;
};

export const AccountSelectionStep = ({
  accountSelectionToken,
  accounts,
  onSelected,
  onBack,
  translations,
  errorCodes
}: Props) => {
  const texts: AccountSelectionTranslations = {
    chooseAccount: translations?.chooseAccount ?? commonTexts.smsLoginTranslations.chooseAccount,
    chooseAccountDescription:
      translations?.chooseAccountDescription ?? commonTexts.smsLoginTranslations.chooseAccountDescription,
    back: translations?.back ?? commonTexts.smsLoginTranslations.back,
    personalAccountPrivate:
      translations?.personalAccountPrivate ?? commonTexts.smsLoginTranslations.personalAccountPrivate,
    personalAccountBusiness:
      translations?.personalAccountBusiness ?? commonTexts.smsLoginTranslations.personalAccountBusiness
  };

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const { mutate, isPending, error } = noddiAsync.usePost({
    type: URLKeys.postSelectAccount,
    queryConfig: {
      onSuccess(res) {
        const outcome = getPhoneLoginOutcome(res.data);
        if (outcome?.kind === PhoneLoginKind.LOGGED_IN) {
          onSelected(outcome);
        }
      },
      onSettled() {
        setSelectedUserId(null);
      }
    }
  });

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1'>
        <p className='font-semibold'>{texts.chooseAccount}</p>
        <p className='text-sm text-muted-foreground'>{texts.chooseAccountDescription}</p>
      </div>
      <ul className='flex flex-col gap-2'>
        {accounts.map((account) => {
          const isSelecting = isPending && selectedUserId === account.id;
          const purpose = purposeLabel(account.personalAccountPurpose, texts);
          return (
            <li key={account.id}>
              <button
                type='button'
                disabled={isPending}
                className='flex w-full flex-col gap-0.5 rounded-lg border border-border bg-background px-4 py-3 text-left transition-colors hover:bg-muted/50 disabled:opacity-60'
                onClick={() => {
                  setSelectedUserId(account.id);
                  mutate({ accountSelectionToken, userId: account.id });
                }}
              >
                <span className='font-medium'>{account.name}</span>
                {purpose != null && <span className='text-xs text-muted-foreground'>{purpose}</span>}
                {account.email != null && account.email.length > 0 && (
                  <span className='text-sm text-muted-foreground'>{account.email}</span>
                )}
                {isSelecting && <span className='text-xs text-muted-foreground'>Signing in...</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {error && <ApiErrorMessage errorCodes={errorCodes} error={error} />}
      <NoddiButton type='button' variant='link' size='small' onClick={onBack} disabled={isPending}>
        {texts.back}
      </NoddiButton>
    </div>
  );
};
